// Section 24.4 and 24.13: iteration over entity maps is always in sorted id order, so
// no result depends on object key insertion order. Sorting uses UTF-16 code unit order,
// which is locale-independent.
import { isDraft } from 'immer';

/** Committed (non-draft) state objects are immutable, so their sorted keys can be cached. */
const keyCache = new WeakMap<object, readonly string[]>();

/**
 * A draft's own keys. Entity maps hold only string keys, so Reflect.ownKeys equals Object.keys
 * here, and on an Immer draft it skips one getOwnPropertyDescriptor trap per key (Section 2).
 */
function draftKeys(record: object): string[] {
  return Reflect.ownKeys(record) as string[];
}

/** Sorted keys of an entity map. Cached for committed maps; drafts are re-sorted each call. */
export function sortedIds<K extends string>(record: Readonly<Partial<Record<K, unknown>>>): K[] {
  if (!isDraft(record)) {
    const cached = keyCache.get(record);
    if (cached !== undefined) return cached.slice() as K[];
    const keys = Object.keys(record).sort();
    keyCache.set(record, keys);
    return keys.slice() as K[];
  }
  return draftKeys(record).sort() as K[];
}

/** Sorted keys without a defensive copy (internal: callers here never mutate it). */
function keysOf<K extends string>(record: Readonly<Partial<Record<K, unknown>>>): readonly K[] {
  if (isDraft(record)) return draftKeys(record).sort() as K[];
  let cached = keyCache.get(record);
  if (cached === undefined) {
    cached = Object.keys(record).sort();
    keyCache.set(record, cached);
  }
  return cached as readonly K[];
}

/** How many entries an entity map has (a draft's keys are listed without descriptor traps). */
export function countIds(record: Readonly<Record<string, unknown>>): number {
  return keysOf(record).length;
}

/**
 * Frozen maps' values in sorted id order (Section 2 budget). Only frozen maps are cached here: a
 * plain object built inside a phase may still have a value replaced. (peopleSorted caches a
 * committed state's people map itself.)
 */
const valueCache = new WeakMap<object, readonly unknown[]>();

/** Values of an entity map in sorted id order (cached for frozen maps; a copy each call). */
export function sortedValues<K extends string, V>(record: Readonly<Partial<Record<K, V>>>): V[] {
  const frozen = Object.isFrozen(record);
  if (frozen) {
    const cached = valueCache.get(record);
    if (cached !== undefined) return cached.slice() as V[];
  }
  const out: V[] = [];
  for (const id of keysOf(record)) {
    const value = record[id];
    if (value !== undefined) out.push(value);
  }
  if (frozen) valueCache.set(record, out.slice());
  return out;
}

/**
 * Values of a committed state's entity map in sorted id order, cached per map (a copy each call).
 * Only for maps owned by a GameState that is not a draft: those never change in place (Immer
 * copies on write), whether or not they are frozen.
 */
export function committedValues<K extends string, V>(record: Readonly<Partial<Record<K, V>>>): V[] {
  if (isDraft(record)) return sortedValues(record);
  let cached = valueCache.get(record) as V[] | undefined;
  if (cached === undefined) {
    cached = [];
    for (const id of keysOf(record)) {
      const value = record[id];
      if (value !== undefined) cached.push(value);
    }
    valueCache.set(record, cached);
  }
  return cached.slice();
}

/** The only iteration allowed over entity maps in state (24.4). */
export function forEachSorted<K extends string, V>(
  record: Readonly<Partial<Record<K, V>>>,
  fn: (value: V, id: K) => void,
): void {
  for (const id of keysOf(record)) {
    const value = record[id];
    if (value !== undefined) fn(value, id);
  }
}
